/**
 * Public Help Center API service
 * No authentication required for these endpoints
 */

const API_BASE = '';

export interface HelpCategory {
  slug: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  article_count: number;
}

export interface HelpArticle {
  slug: string;
  title: string;
  content?: string;
  excerpt?: string;
  category: string;
  category_name?: string;
  updated_at?: string;
}

export interface SearchResult {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  category_name: string;
  similarity: number;
}

// Strip markdown formatting from text for display
export function stripMarkdown(text: string): string {
  if (!text) return '';
  return text
    .replace(/#{1,6}\s+/g, '') // headers
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1') // italic
    .replace(/__([^_]+)__/g, '$1') // bold alt
    .replace(/_([^_]+)_/g, '$1') // italic alt
    .replace(/`([^`]+)`/g, '$1') // inline code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/^\s*[-*+]\s+/gm, '') // list items
    .replace(/^\s*\d+\.\s+/gm, '') // numbered lists
    .replace(/>\s+/g, '') // blockquotes
    .replace(/\n+/g, ' ') // newlines to spaces
    .trim();
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(`${API_BASE}${url}`);
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  }
  return res.json();
}

export const helpService = {
  async getCategories(): Promise<HelpCategory[]> {
    const data = await fetchJson<{ categories: HelpCategory[] }>('/api/help/categories');
    return data.categories;
  },

  async getCategory(slug: string): Promise<{ category: { slug: string; name: string }; articles: HelpArticle[] }> {
    const data = await fetchJson<{ category: { slug: string; name: string }; articles: HelpArticle[] }>(
      `/api/help/category/${slug}`
    );
    return data;
  },

  async getArticle(slug: string): Promise<{ article: HelpArticle; related_articles: HelpArticle[] }> {
    const data = await fetchJson<{ article: HelpArticle; related_articles: HelpArticle[] }>(
      `/api/help/articles/${slug}`
    );
    return data;
  },

  async search(query: string, limit = 10): Promise<{ results: SearchResult[]; total: number }> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    const data = await fetchJson<{ results: SearchResult[]; total: number }>(
      `/api/help/search?${params}`
    );
    return data;
  },

  async getPopularArticles(): Promise<HelpArticle[]> {
    const data = await fetchJson<{ articles: HelpArticle[] }>('/api/help/popular');
    return data.articles;
  },
};
